import type { InferContractRouterInputs } from '@orpc/contract';
import { listEgovApplicationStepsByTagContract } from '@standandforce/api-contract';
import type { ApiResponseBody, ApiSearchQuery } from '@/lib/api/api-type-helper';
import { apiClient } from '@/lib/api/client';
import { getDataOrThrowApiError } from '@/lib/api/error';
import { orpcClient } from '@/lib/api/orpc/client';
import type { ListEgovApplicationStepsByPackageInstancesInput, ListEgovApplicationStepsByTagOutput, ListEgovApplicationStepsByTagStepRowOutput } from '@/lib/api/orpc/contracts/labor-core-egov-application-steps';
import type { StartLaborCoreOperationPackagePatternInput, StartLaborCoreOperationPackagePatternOutput } from '@/lib/api/orpc/contracts/labor-core-start-operation-package-pattern';
import type { CountYearEndAdjustmentInstancesByTagDocumentTabsInput, CountYearEndAdjustmentInstancesByTagDocumentTabsOutput, GetYearEndAdjustmentInstanceDocumentsOutput, ListYearEndAdjustmentInstancesByTagInput, ListYearEndAdjustmentInstancesByTagOutput, ListYearEndAdjustmentInstancesByTagRowOutput } from '@/lib/api/orpc/contracts/labor-core-year-end-adjustment-instances';
export const OWNER_PRODUCTS = ['HRF_LABOR_CORE', 'HRF_CONTRACTOR_CORE', 'IT_OPERATION_CORE', 'GA_OPERATION_CORE', 'STACKTRACE_PN', 'STACKTRACE_QA', 'STACKTRACE_PP'] as const;
export type OwnerProduct = (typeof OWNER_PRODUCTS)[number];

// --- Get Operation Packages ---

export type OperationPackagesResponse = ApiResponseBody<'/v2/boe/packages/{ownerProduct}', 'get'>;
export type PackageType = OperationPackagesResponse[number]['packageType'];
export const getOperationPackages = async (ownerProduct: OwnerProduct): Promise<OperationPackagesResponse> => {
  const res = await apiClient.GET('/v2/boe/packages/{ownerProduct}', {
    params: {
      path: {
        ownerProduct
      }
    }
  });
  return getDataOrThrowApiError(res);
};

// --- Get Operation Package Count In Progress ---

export type OperationPackageCountInProgressResponse = ApiResponseBody<'/v2/boe/packages/{ownerProduct}/{id}/count-in-progress', 'get'>;
export const getOperationPackageCountInProgress = async (ownerProduct: OwnerProduct, id: string): Promise<OperationPackageCountInProgressResponse> => {
  const res = await apiClient.GET('/v2/boe/packages/{ownerProduct}/{id}/count-in-progress', {
    params: {
      path: {
        ownerProduct,
        id
      }
    }
  });
  return getDataOrThrowApiError(res);
};

// --- Get Operation Package Patterns ---

export type OperationPackagePatternsResponse = ApiResponseBody<'/v2/boe/packages/{ownerProduct}/{id}/patterns', 'get'>;
export const getOperationPackagePatterns = async (ownerProduct: OwnerProduct, id: string): Promise<OperationPackagePatternsResponse> => {
  const res = await apiClient.GET('/v2/boe/packages/{ownerProduct}/{id}/patterns', {
    params: {
      path: {
        ownerProduct,
        id
      }
    }
  });
  return getDataOrThrowApiError(res);
};

// --- Post Operation Package Patterns ---

// POST の OpenAPI 定義が Record<string, never> になっているが、実行時は GET と同型の配列を返す
export type PostOperationPackagePatternsResponse = OperationPackagePatternsResponse;
export const postOperationPackagePatterns = async (ownerProduct: OwnerProduct, id: string, body?: {
  name: string;
  description?: string;
}): Promise<PostOperationPackagePatternsResponse> => {
  const res = await apiClient.POST('/v2/boe/packages/{ownerProduct}/{id}/patterns', {
    params: {
      path: {
        ownerProduct,
        id
      }
    },
    body: body ?? {}
  });
  return getDataOrThrowApiError(res) as unknown as PostOperationPackagePatternsResponse;
};

// --- Assign Task To Person ---

export type StartPatternResponse = ApiResponseBody<'/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}', 'post'>;
export type StartPatternRequestBody = {
  targetPersonIds: string[];
  anchorDate: string;
  /** タグ名（手続きインスタンスに付与する運用ラベル） */
  tagName?: string;
};
export const startPattern = async (ownerProduct: OwnerProduct, id: string, patternId: string, body: StartPatternRequestBody): Promise<StartPatternResponse> => {
  const res = await apiClient.POST('/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}', {
    params: {
      path: {
        ownerProduct,
        id,
        patternId
      }
    },
    body
  });
  return getDataOrThrowApiError(res);
};

// --- Get Operation Task Count In Progress ---

export type OperationTaskCountInProgressResponse = ApiResponseBody<'/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}/count-in-progress', 'get'>;
export const getOperationTaskCountInProgress = async (ownerProduct: OwnerProduct, id: string, patternId: string): Promise<OperationTaskCountInProgressResponse> => {
  const res = await apiClient.GET('/v2/boe/packages/{ownerProduct}/{id}/patterns/{patternId}/count-in-progress', {
    params: {
      path: {
        ownerProduct,
        id,
        patternId
      }
    }
  });
  return getDataOrThrowApiError(res);
};

// --- Get Target Persons (it-operation-core: 対象者一覧。デバイス手配・アカウント一括発行で利用) ---

const IT_OPERATION_TARGET_PERSONS_PATH = '/v2/it-operation-core/packages/{packageId}/patterns/{patternId}/target-persons' as const;
export type ItOperationTargetPersonsResponse = ApiResponseBody<typeof IT_OPERATION_TARGET_PERSONS_PATH, 'get'>;
export type ItOperationTargetPersonsQuery = ApiSearchQuery<typeof IT_OPERATION_TARGET_PERSONS_PATH, 'get'>;
export const getItOperationTargetPersons = async (packageId: string, patternId: string, query: ItOperationTargetPersonsQuery): Promise<ItOperationTargetPersonsResponse> => {
  const res = await apiClient.GET(IT_OPERATION_TARGET_PERSONS_PATH, {
    params: {
      path: {
        packageId,
        patternId
      },
      query
    }
  });
  return getDataOrThrowApiError(res);
};

// --- Start Labor Core Pattern (supports both targetPersonIds and targetContractIds) ---

export type StartLaborCorePatternResponse = ApiResponseBody<'/v2/labor-core/operation-packages/{id}/patterns/{patternId}', 'post'> | StartLaborCoreOperationPackagePatternOutput;
export type StartLaborCorePatternRequestBody = {
  targetPersonIds?: string[];
  targetContractIds?: string[];
  /**
   * 対象者ごとの起動指定。指定時は targetPersonIds より優先。
   * targetPersonIds との同時指定は不可（422）。
   */
  targets?: StartLaborCoreOperationPackagePatternInput['targets'];
  anchorDate: string;
  /** タグ名（手続きインスタンスに付与する運用ラベル） */
  tagName?: string;
};

/**
 * Labor Core パターン起動。
 * `targets`（GovProcedureType 付き）は api-contract 由来のため oRPC 経由。
 * `targetPersonIds` / `targetContractIds` のみの従来パスは openapi-fetch を継続利用。
 */
export const startLaborCorePattern = async (id: string, patternId: string, body: StartLaborCorePatternRequestBody): Promise<StartLaborCorePatternResponse> => {
  if (body.targets !== undefined && body.targets.length > 0) {
    return orpcClient.laborCoreOperationPackage.startPattern({
      id,
      patternId,
      anchorDate: body.anchorDate,
      tagName: body.tagName,
      targets: body.targets
    });
  }
  const res = await apiClient.POST('/v2/labor-core/operation-packages/{id}/patterns/{patternId}', {
    params: {
      path: {
        id,
        patternId
      }
    },
    body: {
      anchorDate: body.anchorDate,
      tagName: body.tagName,
      targetPersonIds: body.targetPersonIds,
      targetContractIds: body.targetContractIds
    }
  });
  return getDataOrThrowApiError(res);
};

// --- Start Contractor Core Pattern (supports both targetPersonIds and targetContractIds) ---

export type StartContractorCorePatternResponse = ApiResponseBody<'/v2/contractor-core/operation-packages/{id}/patterns/{patternId}', 'post'>;
export type StartContractorCorePatternRequestBody = {
  targetPersonIds?: string[];
  targetContractIds?: string[];
  anchorDate: string;
  /** タグ名（手続きインスタンスに付与する運用ラベル） */
  tagName?: string;
};
export const startContractorCorePattern = async (id: string, patternId: string, body: StartContractorCorePatternRequestBody): Promise<StartContractorCorePatternResponse> => {
  const res = await apiClient.POST('/v2/contractor-core/operation-packages/{id}/patterns/{patternId}', {
    params: {
      path: {
        id,
        patternId
      }
    },
    body
  });
  return getDataOrThrowApiError(res);
};

// --- Get Operation Package Instances ---

export type OperationPackageInstancesResponse = ApiResponseBody<'/v2/boe/packages/{ownerProduct}/instances', 'get'>;
export type OperationPackageInstanceRow = OperationPackageInstancesResponse['items'][number];
export const getOperationPackageInstances = async (ownerProduct: OwnerProduct, patternId: string, options?: {
  page?: number;
  pageSize?: number;
  targetId?: string;
}): Promise<OperationPackageInstancesResponse> => {
  const res = await apiClient.GET('/v2/boe/packages/{ownerProduct}/instances', {
    params: {
      path: {
        ownerProduct
      },
      query: {
        patternId,
        ...options
      }
    }
  });
  return getDataOrThrowApiError(res);
};

/**
 * Labor 行政申請（手続き系）の Operation Package Instance 一覧（Step 列付き）。
 * バックエンド: `GET /boe/packages/:ownerProduct/instances/with-step-columns`
 * （クエリは `GET .../instances` と同様。OpenAPI 上は `/v2` プレフィックス付き）
 */
const GOVERNMENT_APPLICATION_PACKAGE_INSTANCES_GET = '/v2/boe/packages/{ownerProduct}/instances/with-step-columns' as const;
export type GovernmentApplicationPackageInstancesResponse = ApiResponseBody<typeof GOVERNMENT_APPLICATION_PACKAGE_INSTANCES_GET, 'get'>;
export const getGovernmentApplicationPackageInstances = async (ownerProduct: OwnerProduct, patternId: string, options?: {
  page?: number;
  pageSize?: number;
  targetId?: string;
}): Promise<GovernmentApplicationPackageInstancesResponse> => {
  const res = await apiClient.GET(GOVERNMENT_APPLICATION_PACKAGE_INSTANCES_GET, {
    params: {
      path: {
        ownerProduct
      },
      query: {
        patternId,
        ...options
      }
    }
  });
  return getDataOrThrowApiError(res);
};

/**
 * セルフレポート等の freeForm 設問列付き Operation Package Instance 一覧。
 * バックエンド: `GET /boe/packages/:ownerProduct/instances/with-free-form-columns`
 */
const SELF_REPORT_FREE_FORM_COLUMN_INSTANCES_GET = '/v2/boe/packages/{ownerProduct}/instances/with-free-form-columns' as const;
export type SelfReportFreeFormColumnInstancesResponse = ApiResponseBody<typeof SELF_REPORT_FREE_FORM_COLUMN_INSTANCES_GET, 'get'>;
export const getSelfReportFreeFormColumnInstances = async (ownerProduct: OwnerProduct, patternId: string, options?: {
  page?: number;
  pageSize?: number;
  targetId?: string;
  tagId?: string;
}): Promise<SelfReportFreeFormColumnInstancesResponse> => {
  const res = await apiClient.GET(SELF_REPORT_FREE_FORM_COLUMN_INSTANCES_GET, {
    params: {
      path: {
        ownerProduct
      },
      query: {
        patternId,
        ...options
      }
    }
  });
  return getDataOrThrowApiError(res);
};

/**
 * セルフレポート確認画面用の自由フォーム回答値グリッド。
 * バックエンド: `GET /boe/packages/:ownerProduct/instances/free-form-answers`
 */
const SELF_REPORT_CONFIRMATION_FREE_FORM_ANSWERS_GET = '/v2/boe/packages/{ownerProduct}/instances/free-form-answers' as const;
export type SelfReportConfirmationFreeFormAnswersResponse = ApiResponseBody<typeof SELF_REPORT_CONFIRMATION_FREE_FORM_ANSWERS_GET, 'get'>;
export const getSelfReportConfirmationFreeFormAnswers = async (ownerProduct: OwnerProduct, patternId: string, options?: {
  page?: number;
  pageSize?: number;
  targetId?: string;
  tagId?: string;
}): Promise<SelfReportConfirmationFreeFormAnswersResponse> => {
  const res = await apiClient.GET(SELF_REPORT_CONFIRMATION_FREE_FORM_ANSWERS_GET, {
    params: {
      path: {
        ownerProduct
      },
      query: {
        patternId,
        ...options
      }
    }
  });
  return getDataOrThrowApiError(res);
};

/**
 * Labor Core 専用: タグ別・行政申請系パッケージの Step 一覧（確認画面用）。
 * oRPC: `listEgovApplicationStepsByTagContract`
 */
export type EgovApplicationStepsByTagResponse = ListEgovApplicationStepsByTagOutput;

/** 一覧 API の Package グループ行 */
export type EgovApplicationPackageGroupByTagRow = ListEgovApplicationStepsByTagOutput['items'][number];

/** Step 行（ドロワー・一括操作向けに親 Package ID を付与したビュー型） */
export type EgovApplicationStepByTagRow = ListEgovApplicationStepsByTagStepRowOutput & {
  operationPackageInstanceId: string;
};
type ListEgovApplicationStepsByTagInput = InferContractRouterInputs<typeof listEgovApplicationStepsByTagContract>;
export type EgovApplicationStepsByTagQuery = Omit<ListEgovApplicationStepsByTagInput, 'tagId'>;
export const getEgovApplicationStepsByTag = async (tagId: string, options: EgovApplicationStepsByTagQuery): Promise<EgovApplicationStepsByTagResponse> => {
  return orpcClient.laborCoreEgovApplicationSteps.listByTag({
    tagId,
    operationPackagePatternId: options.operationPackagePatternId,
    page: options.page,
    pageSize: options.pageSize,
    orderBy: options.orderBy,
    orderDirection: options.orderDirection,
    searchText: options.searchText,
    status: options.status ? [...options.status] : undefined
  });
};

/**
 * Labor Core 専用: アーカイブ済み手続きの行政申請 Step 一覧（Package Instance 指定）。
 * oRPC: `listEgovApplicationStepsByPackageInstancesContract`
 */
export type EgovApplicationStepsByPackageInstancesResponse = ListEgovApplicationStepsByTagOutput;
export type EgovApplicationStepsByPackageInstancesQuery = Omit<ListEgovApplicationStepsByPackageInstancesInput, 'operationPackageInstanceIds'>;
export const getEgovApplicationStepsByPackageInstances = async (operationPackageInstanceIds: readonly string[], options: EgovApplicationStepsByPackageInstancesQuery): Promise<EgovApplicationStepsByPackageInstancesResponse> => {
  return orpcClient.laborCoreEgovApplicationSteps.listByPackageInstances({
    operationPackageInstanceIds: [...operationPackageInstanceIds],
    page: options.page,
    pageSize: options.pageSize,
    orderBy: options.orderBy,
    orderDirection: options.orderDirection,
    searchText: options.searchText,
    status: options.status ? [...options.status] : undefined
  });
};

/**
 * Labor Core: 年末調整 内容確認（タグ別インスタンス一覧）。
 * oRPC: `listYearEndAdjustmentInstancesByTagContract`
 */
export type YearEndAdjustmentInstancesByTagResponse = ListYearEndAdjustmentInstancesByTagOutput;
export type YearEndAdjustmentInstanceByTagRow = ListYearEndAdjustmentInstancesByTagRowOutput;
export type YearEndAdjustmentInstancesByTagQuery = Omit<ListYearEndAdjustmentInstancesByTagInput, 'tagId'>;
export const getYearEndAdjustmentInstancesByTag = async (tagId: string, options: YearEndAdjustmentInstancesByTagQuery): Promise<YearEndAdjustmentInstancesByTagResponse> => {
  return orpcClient.laborCoreYearEndAdjustmentInstances.listByTag({
    tagId,
    page: options.page,
    pageSize: options.pageSize,
    orderBy: options.orderBy,
    orderDirection: options.orderDirection,
    searchText: options.searchText,
    status: options.status,
    documentFocus: options.documentFocus
  });
};

/**
 * Labor Core: 年末調整 内容確認（書類タブ別件数）。
 * oRPC: `countYearEndAdjustmentInstancesByTagDocumentTabsContract`
 */
export type YearEndAdjustmentDocumentTabCountsResponse = CountYearEndAdjustmentInstancesByTagDocumentTabsOutput;
export type YearEndAdjustmentDocumentTabCountsQuery = Omit<CountYearEndAdjustmentInstancesByTagDocumentTabsInput, 'tagId'>;
export const getYearEndAdjustmentDocumentTabCounts = async (tagId: string, options: YearEndAdjustmentDocumentTabCountsQuery): Promise<YearEndAdjustmentDocumentTabCountsResponse> => {
  return orpcClient.laborCoreYearEndAdjustmentInstances.documentTabCounts({
    tagId,
    searchText: options.searchText,
    status: options.status
  });
};

/**
 * Labor Core: 年末調整 インスタンス書類一覧（ドロワー用・itemKey でキー付け）。
 * oRPC: `getYearEndAdjustmentInstanceDocumentsContract`
 */
export type YearEndAdjustmentInstanceDocumentsResponse = GetYearEndAdjustmentInstanceDocumentsOutput;
export const getYearEndAdjustmentInstanceDocuments = async (operationPackageInstanceId: string): Promise<YearEndAdjustmentInstanceDocumentsResponse> => {
  return orpcClient.laborCoreYearEndAdjustmentInstances.instanceDocuments({
    operationPackageInstanceId
  });
};
const PACKAGE_INSTANCE_TAG_GET = '/v2/boe/packages/{ownerProduct}/instance-tags/{tagId}' as const;
export type PackageInstanceTagResponse = ApiResponseBody<typeof PACKAGE_INSTANCE_TAG_GET, 'get'>;
export const getPackageInstanceTag = async (ownerProduct: OwnerProduct, tagId: string): Promise<PackageInstanceTagResponse> => {
  const res = await apiClient.GET(PACKAGE_INSTANCE_TAG_GET, {
    params: {
      path: {
        ownerProduct,
        tagId
      }
    }
  });
  return getDataOrThrowApiError(res);
};